import React, {
    useState,
    useEffect,
    useCallback,
    MutableRefObject
} from "react";

import {
    GlobalNavLinkContentProps,
    useCurrentNav
} from "./nav-links-container";

export type GlobalNavLinkProps = GlobalNavLinkContentProps & {
    dropdownRef: MutableRefObject<HTMLDivElement | null>;
};

const GlobalNavLink = (props: GlobalNavLinkProps) => {
    const { current, setCurrent } = useCurrentNav();
    const { dropdownRef } = props;

    const isCurrentOpenDropdown = current === props.globalLinksKey;

    const [isOpen, setIsOpen] = useState(false);

    const anchorLinkClassName = `nav-link-anchor 
    ${!!isCurrentOpenDropdown && !!props.hasSubnav ? "isActive" : ""} 
    ${!props.hasSubnav ? "isHoverableLink" : ""} 
    ${props.isActive ? "isActive" : ""}`;

    const linkClickAction: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        if (props.hasSubnav) {
            e.preventDefault();
            if (isOpen) {
                setCurrent("");
                setIsOpen(false);
            } else {
                setCurrent(props.globalLinksKey);
                setIsOpen(true);
            }
        }
    };

    const handleEscKeyEvent = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape" && isCurrentOpenDropdown) {
                setCurrent("");
                setIsOpen(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isCurrentOpenDropdown]
    );

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setCurrent("");
                setIsOpen(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKeyEvent, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent, handleClickOutside]);

    return (
        <>
            <a
                className={anchorLinkClassName}
                href={props.href ?? "#"}
                onClick={linkClickAction}
                onMouseEnter={() => {
                    if (!isCurrentOpenDropdown) {
                        setCurrent(props.globalLinksKey);
                        setIsOpen(true);
                    }
                }}
                aria-label={props.linkText}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="headerDropdownLink"
                data-product-key={props.productKey ?? "jiraSoftware"}
            >
                {props.linkText}
                {!!props.hasSubnav && (
                    <div className="chevron-icon">
                        {isCurrentOpenDropdown ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="11"
                                height="6"
                                viewBox="0 0 11 6"
                                fill="none"
                            >
                                <path
                                    d="M1.19922 4.81824L5.19922 1.18187L9.19922 4.81824"
                                    stroke="#1868DB"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="11"
                                height="6"
                                viewBox="0 0 11 6"
                                fill="none"
                            >
                                <path
                                    d="M9.19922 1.18176L5.19922 4.81813L1.19922 1.18176"
                                    stroke="#101214"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </div>
                )}
            </a>
        </>
    );
};

export default GlobalNavLink;
